import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBranding } from "../components/Layout";
import { Home, CheckCircle2 } from "lucide-react";

export default function Invite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const branding = useBranding();
  const [seller, setSeller] = useState(null);
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invite/${token}`);
        setSeller(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || "Invite not found.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/invite/${token}/accept`, { password });
      if (data.access_token) localStorage.setItem("sellerhub_token", data.access_token);
      await refresh();
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  if (loading) return <div className="container-x" style={{ padding: "64px 0", color: "var(--text-muted)" }}>Loading…</div>;

  if (!seller) {
    return (
      <div className="container-x" style={{ padding: "64px 0", display: "flex", justifyContent: "center" }}>
        <div className="card" style={{ padding: 40, maxWidth: 460, textAlign: "center" }} data-testid="invite-invalid">
          <span className="overline">Invite unavailable</span>
          <h1 className="font-serif" style={{ fontSize: 32, margin: "12px 0", fontWeight: 500 }}>Hmm, this link won't open.</h1>
          <p style={{ color: "var(--text-muted)" }}>{err || "The invite may have been used or expired."}</p>
          <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 16 }}>Ask {branding.agent_first_name} to send you a new invite{branding.agent_email ? <> at <a href={`mailto:${branding.agent_email}`} style={{ color: "var(--primary)" }}>{branding.agent_email}</a></> : null}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x" style={{ padding: "48px 0 64px", display: "flex", justifyContent: "center" }}>
      <div style={{ maxWidth: 500, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, color: "var(--primary)" }}>
          <Home size={18} /> <span className="overline" style={{ color: "var(--primary)" }}>Welcome</span>
        </div>
        <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 5vw, 3rem)", fontWeight: 500, letterSpacing: "-0.01em", margin: "0 0 12px" }}>
          Hi {seller.name.split(" ")[0]}!<br />
          <em style={{ fontStyle: "italic", color: "var(--secondary)" }}>Let's get you set up.</em>
        </h1>
        <p style={{ color: "var(--text-muted)", fontSize: "1.05rem", lineHeight: 1.5 }}>
          {branding.agent_first_name} has created your {branding.product_name} account for <b style={{ color: "var(--text)" }}>{seller.property_address || seller.email}</b>. Choose a password to activate it.
        </p>

        <form onSubmit={submit} className="card" style={{ padding: 32, marginTop: 28 }} data-testid="invite-form">
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 14, fontWeight: 500, display: "block", marginBottom: 6 }}>Email</label>
            <input className="input-field" value={seller.email} disabled data-testid="invite-email" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 14, fontWeight: 500, display: "block", marginBottom: 6 }}>Create a password</label>
            <input data-testid="invite-password" className="input-field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="At least 6 characters" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 14, fontWeight: 500, display: "block", marginBottom: 6 }}>Confirm password</label>
            <input data-testid="invite-confirm" className="input-field" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} />
          </div>
          {err && <div data-testid="invite-error" style={{ color: "var(--error)", fontSize: 14, marginBottom: 12 }}>{err}</div>}
          <button data-testid="invite-submit-btn" type="submit" className="btn-primary" disabled={busy} style={{ justifyContent: "center", width: "100%" }}>
            {busy ? "Activating…" : (<><CheckCircle2 size={16} /> Activate my account</>)}
          </button>
          <p style={{ color: "var(--text-muted)", fontSize: 12, textAlign: "center", marginTop: 14 }}>
            By activating, you agree to work with {branding.agent_name}{branding.firm ? ` of ${branding.firm}` : ""}.
          </p>
        </form>
      </div>
    </div>
  );
}
